import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { Input, cx } from "./ui";
import type { AiModel } from "../lib/types";

/**
 * Which models a key may call.
 *
 * Two states, and the shape of the control says which one a key is in. `null`
 * is "any listed model" — a sentence and one button, because that is what
 * every key was before this existed and what most keys should stay. A list is
 * the restricted state: a search box over the catalogue, the picks as chips
 * above it, and a way back to "any". An empty list is a state the picker can
 * be *in* while somebody is choosing, and never one it will submit — the
 * parent disables the save until there is at least one, and the hint says so.
 *
 * Checkboxes rather than a multi-select `<select>`, because the catalogue is
 * four hundred rows and a native multi-select shows eight of them with no
 * search. The list is capped in height and scrolls; the search is what
 * somebody actually uses on it.
 */
export function ModelPicker({
  id,
  models,
  value,
  onChange,
  disabled = false,
}: {
  id: string;
  models: AiModel[];
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("settings");
  const [query, setQuery] = useState("");
  const sorted = useMemo(() => [...models].sort((a, b) => a.id.localeCompare(b.id)), [models]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter(
      (model) =>
        model.id.toLowerCase().includes(needle) ||
        model.name.toLowerCase().includes(needle) ||
        model.owned_by.toLowerCase().includes(needle),
    );
  }, [sorted, query]);

  if (value === null) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-canvas px-3 py-2.5">
        <p className="text-[13px] text-fg-muted">{t("modelPicker.any")}</p>
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => onChange([])}
          className="text-[13px] text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover disabled:text-fg-subtle"
        >
          {t("modelPicker.restrict")}
        </button>
      </div>
    );
  }

  const selected = new Set(value);
  const toggle = (modelId: string) => {
    const next = new Set(selected);
    if (next.has(modelId)) next.delete(modelId);
    else next.add(modelId);
    onChange([...next].sort());
  };

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label={t("modelPicker.allowed")}>
          {value.map((modelId) => (
            <li
              key={modelId}
              className="inline-flex items-center gap-1 rounded-md border border-line bg-raised py-0.5 pr-1 pl-2 font-mono text-[11px] text-fg"
            >
              {modelId}
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggle(modelId)}
                aria-label={`Remove ${modelId}`}
                className="grid size-4 place-items-center rounded text-fg-subtle transition-colors duration-200 ease-smooth hover:text-fg"
              >
                <Icon name="close" className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input
        id={id}
        type="search"
        value={query}
        disabled={disabled}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t("modelPicker.search")}
        aria-label={t("modelPicker.search")}
      />
      <ul
        role="listbox"
        aria-multiselectable="true"
        className="max-h-56 divide-y divide-line overflow-y-auto rounded-md border border-line bg-canvas"
      >
        {shown.length === 0 ? (
          <li className="px-3 py-2.5 text-[13px] text-fg-subtle">{t("modelPicker.noMatch")}</li>
        ) : (
          shown.map((model) => {
            const checked = selected.has(model.id);
            return (
              <li key={model.id} role="option" aria-selected={checked}>
                <label
                  className={cx(
                    "flex cursor-pointer items-center gap-2.5 px-3 py-2 text-[13px]",
                    checked ? "text-fg" : "text-fg-muted",
                    disabled && "cursor-not-allowed opacity-60",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(model.id)}
                    className="size-3.5 accent-accent"
                  />
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-mono">{model.id}</span>
                    <span className="ml-2 text-fg-subtle">{model.name}</span>
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
      <div className="flex items-center justify-between gap-3 text-[13px]">
        <p className={cx(value.length === 0 ? "text-warn" : "text-fg-subtle")}>
          {value.length === 0
            ? t("modelPicker.pickOne")
            : t("modelPicker.allowedCount", { count: value.length })}
        </p>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(null)}
          className="shrink-0 text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover disabled:text-fg-subtle"
        >
          {t("modelPicker.allowAny")}
        </button>
      </div>
    </div>
  );
}
